import { canAccessPage, initials, roleLabel, type Page, type Role } from '@agrogruda/shared';
import logo from '@/assets/logo.png';
import { NAV_ICONS } from '@/components/icons/NavIcons';
import { NAV_PAGES } from '@/config/pages';
import { TaskNavBadge } from '@/features/tasks/TaskNavBadge';
import { NAV_LABELS, sq } from '@/i18n/sq';
import { cx } from '@/lib/cx';
import { NotificationsButton } from '@/push/NotificationsButton';

export interface SidebarProps {
  open: boolean;
  activePage: Page;
  userName: string;
  role: Role | null;
  canManageUsers: boolean;
  onNavigate: (page: Page) => void;
  onOpenUsers: () => void;
  onLogout: () => void;
}

/** Prototype <aside class="sidebar"> (lines 899-942): brand, nav buttons with badge slots, user footer. */
export function Sidebar({
  open,
  activePage,
  userName,
  role,
  canManageUsers,
  onNavigate,
  onOpenUsers,
  onLogout,
}: SidebarProps) {
  return (
    <aside className={cx('sidebar', open && 'open')} id="sidebar">
      <div className="brand">
        <div className="brand-logo">
          <img alt={sq.app.logoAlt} src={logo} />
        </div>
      </div>
      <div className="nav-label">{sq.nav.menu}</div>
      <nav className="nav" id="nav" aria-label={sq.nav.menu}>
        {NAV_PAGES.filter((page) => canAccessPage(role, page)).map((page) => {
          const active = page === activePage;
          return (
            <button
              type="button"
              key={page}
              className={cx('nav-btn', active && 'active')}
              data-page={page}
              aria-current={active ? 'page' : undefined}
              onClick={() => onNavigate(page)}
            >
              {NAV_ICONS[page]}
              {NAV_LABELS[page]}
              {page === 'tasks' ? <TaskNavBadge /> : null}
              {page === 'whatsapp' ? (
                <>
                  {' '}
                  <span className="wa-nav-badge hidden" id="waNavBadge">
                    0
                  </span>
                </>
              ) : null}
              <span className="nav-dot" />
            </button>
          );
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="user-row">
          <div className="avatar" id="sidebarUserAvatar" aria-hidden="true">
            {initials(userName)}
          </div>
          <div>
            <b id="sidebarUserName">{userName}</b>
            <small id="sidebarUserRole">{roleLabel(role)}</small>
          </div>
        </div>
        <div className="sidebar-user-actions">
          {canManageUsers ? (
            <button type="button" className="btn small" id="manageUsersBtn" onClick={onOpenUsers}>
              {sq.nav.users}
            </button>
          ) : null}
          <NotificationsButton />
          <button type="button" className="btn small" id="logoutBtn" onClick={onLogout}>
            {sq.nav.logout}
          </button>
        </div>
      </div>
    </aside>
  );
}
